import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Button asChild variant="outline" className="mb-6">
          <a href="/">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </a>
        </Button>

        <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
        <p className="text-gray-600 mb-8">Last Updated: December 2025</p>

        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Compare Before You Buy ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website comparebeforeyoubuy.shop.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Personal Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you contact us or make an inquiry, we may collect:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Postal address</li>
              <li>Product preferences and inquiries</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Automatically Collected Information</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Pages visited and time spent</li>
              <li>Referring website addresses</li>
              <li>Device information</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We use your information to:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Respond to your inquiries and provide customer service</li>
              <li>Process and fulfill orders</li>
              <li>Send you product information and updates (with your consent)</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations</li>
              <li>Prevent fraud and enhance security</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Cookies and Tracking Technologies</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">What Are Cookies?</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Cookies are small text files stored on your device when you visit our website. They help us provide you with a better experience and understand how you use our site.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Types of Cookies We Use</h3>
            
            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <h4 className="font-semibold text-gray-900 mb-2">Essential Cookies</h4>
              <p className="text-gray-700 mb-2">
                Required for the website to function properly. These cannot be disabled.
              </p>
              <ul className="list-disc pl-6 text-gray-700 text-sm">
                <li>Session management</li>
                <li>Security and authentication</li>
                <li>Load balancing</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <h4 className="font-semibold text-gray-900 mb-2">Analytics Cookies</h4>
              <p className="text-gray-700 mb-2">
                Help us understand how visitors interact with our website by collecting and reporting information anonymously.
              </p>
              <ul className="list-disc pl-6 text-gray-700 text-sm">
                <li><strong>Google Analytics</strong>: Tracks page views, session duration, bounce rates, and user demographics</li>
                <li><strong>Google Ads (gtag.js)</strong>: Conversion tracking and remarketing (ID: AW-18129970476)</li>
                <li>Data collected: Pages visited, time on site, browser type, device type, referral source</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <h4 className="font-semibold text-gray-900 mb-2">Advertising Cookies</h4>
              <p className="text-gray-700 mb-2">
                Used to deliver relevant advertisements and track ad campaign performance.
              </p>
              <ul className="list-disc pl-6 text-gray-700 text-sm">
                <li><strong>Google Ads</strong>: Displays targeted ads based on your browsing behavior</li>
                <li><strong>Remarketing pixels</strong>: Show relevant ads on other websites you visit</li>
                <li>These cookies may track your activity across websites</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <h4 className="font-semibold text-gray-900 mb-2">Affiliate Tracking Cookies</h4>
              <p className="text-gray-700 mb-2">
                Track referrals to partner retailers to attribute commissions when you make a purchase.
              </p>
              <ul className="list-disc pl-6 text-gray-700 text-sm">
                <li>Duration: Typically 24 hours to 30 days</li>
                <li>Purpose: Identify purchases made through our affiliate links</li>
                <li>No additional cost to you</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Managing Cookies</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You can control and delete cookies through your browser settings. However, disabling cookies may affect website functionality.
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Chrome</strong>: Settings → Privacy and security → Cookies and other site data</li>
              <li><strong>Firefox</strong>: Settings → Privacy & Security → Cookies and Site Data</li>
              <li><strong>Safari</strong>: Preferences → Privacy → Manage Website Data</li>
              <li><strong>Edge</strong>: Settings → Cookies and site permissions</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Third-Party Analytics & Tracking</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the following third-party services that may collect data:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Google Analytics</strong>: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a></li>
              <li><strong>Google Ads</strong>: <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Advertising Policies</a></li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Opt-Out Options</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Google Analytics Opt-out</strong>: Install the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Analytics Opt-out Browser Add-on</a></li>
              <li><strong>Google Ads Settings</strong>: Manage ad personalization at <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Ads Settings</a></li>
              <li><strong>Industry Opt-out</strong>: Visit <a href="https://www.youronlinechoices.com/uk/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Your Online Choices (UK)</a></li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell your personal information. We may share your information with:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Service providers who assist our business operations</li>
              <li>Payment processors for transaction processing</li>
              <li>Delivery partners for order fulfillment</li>
              <li>Law enforcement when required by law</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement appropriate technical and organizational measures to protect your personal information. However, no internet transmission is completely secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Rights Under GDPR (UK & EU)</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Under the UK General Data Protection Regulation (UK GDPR) and EU GDPR, you have comprehensive rights regarding your personal data:
            </p>
            
            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Your Data Protection Rights</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="font-semibold min-w-[180px]">Right to Access:</span>
                  <span>Request copies of your personal data held by us</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold min-w-[180px]">Right to Rectification:</span>
                  <span>Correct inaccurate or incomplete personal data</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold min-w-[180px]">Right to Erasure:</span>
                  <span>Request deletion of your personal data ("right to be forgotten")</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold min-w-[180px]">Right to Restriction:</span>
                  <span>Request limitation of processing your personal data</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold min-w-[180px]">Right to Portability:</span>
                  <span>Receive your data in a structured, machine-readable format</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold min-w-[180px]">Right to Object:</span>
                  <span>Object to processing based on legitimate interests or direct marketing</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold min-w-[180px]">Right to Withdraw:</span>
                  <span>Withdraw consent at any time (where processing is based on consent)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold min-w-[180px]">Right to Lodge:</span>
                  <span>Complain to the Information Commissioner's Office (ICO) if you believe your rights have been violated</span>
                </li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">How to Exercise Your Rights</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              To exercise any of these rights, contact us at:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Email: <a href="mailto:contact@comparebeforeyoubuy.shop" className="text-blue-600 hover:underline">contact@comparebeforeyoubuy.shop</a></li>
              <li>Phone: <a href="tel:08006521866" className="text-blue-600 hover:underline">08006521866</a> (UK Customer Support)</li>
              <li>Post: 91 CC/A Phase 4 DHA Lahore, Lahore, 54000, Pakistan</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              We will respond to your request within one month. In complex cases, we may extend this by two additional months and will notify you.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Complaints to Regulators</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you're not satisfied with our response, you have the right to lodge a complaint with:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <p className="font-semibold text-gray-900 mb-2">Information Commissioner's Office (ICO)</p>
              <p className="text-gray-700">Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF</p>
              <p className="text-gray-700 mt-2">Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">ico.org.uk</a></p>
              <p className="text-gray-700">Helpline: 0303 123 1113</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7A. Your Rights Under CCPA (California Residents)</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you are a California resident, the California Consumer Privacy Act (CCPA) provides you with additional rights:
            </p>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">CCPA Rights</h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Right to Know:</strong> Request disclosure of personal information collected, used, disclosed, or sold</li>
                <li><strong>Right to Delete:</strong> Request deletion of personal information we have collected</li>
                <li><strong>Right to Opt-Out:</strong> Opt-out of the sale of your personal information (we do not sell personal information)</li>
                <li><strong>Right to Non-Discrimination:</strong> Not be discriminated against for exercising your CCPA rights</li>
              </ul>
            </div>

            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Important Note:</strong> We do not sell personal information to third parties. We may share data with service providers and affiliate partners as described in this policy.
            </p>

            <p className="text-gray-700 leading-relaxed mb-4">
              To exercise CCPA rights, contact us at <a href="mailto:contact@comparebeforeyoubuy.shop" className="text-blue-600 hover:underline">contact@comparebeforeyoubuy.shop</a> with the subject line "CCPA Rights Request".
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We retain your personal information only as long as necessary for the purposes outlined in this policy or as required by law. Typically, we retain customer data for up to 7 years for accounting and legal purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our services are not intended for individuals under 18 years of age. We do not knowingly collect information from children.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page with an updated "Last Updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Affiliate Disclosure & Advertising Transparency</h2>
            
            <div className="bg-orange-50 border-l-4 border-orange-600 p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">🔔 Important Disclosure</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                CompareBeforeYouBuy participates in affiliate marketing programs. This means we may earn commissions when you click on certain links and make purchases through partner retailers. <strong>This does not affect the price you pay</strong> — retailers pay us a commission for referring customers.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">How Affiliate Marketing Works</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you click a product link on our website and make a purchase from a partner retailer, we may receive a small commission. This is a standard practice in the e-commerce industry and helps us:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Maintain and improve our website</li>
              <li>Research and compare products</li>
              <li>Provide free comparison tools and information</li>
              <li>Keep our services free for users</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Editorial Independence</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our affiliate relationships <strong>do not influence</strong> our product rankings, recommendations, or editorial content. We maintain strict editorial independence and base our comparisons on:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Official product specifications from manufacturers</li>
              <li>Customer reviews and satisfaction ratings</li>
              <li>Price comparisons across multiple retailers</li>
              <li>Feature analysis and technical research</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              See our <a href="/editorial-policy" className="text-blue-600 hover:underline">Editorial Policy</a> for more details.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Advertising & Tracking</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use Google Ads (Tracking ID: AW-18129970476) for advertising purposes, which may:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Track conversions and ad performance</li>
              <li>Display personalized advertisements based on your browsing behavior</li>
              <li>Remarket to users who have visited our site</li>
              <li>Collect analytics data to improve our advertising</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              You can opt out of personalized advertising at <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Ads Settings</a>.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Transparency Commitment</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We are committed to full transparency about our business model. All affiliate relationships are disclosed, and we never recommend products solely based on commission rates. Our goal is to help you make informed purchasing decisions.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">About CompareBeforeYouBuy</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              CompareBeforeYouBuy was created to help consumers make more informed purchasing decisions through transparent product comparisons and educational buying guides. Our goal is to simplify online shopping by researching products, comparing features, and presenting information in a clear and user-friendly format.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions about this Privacy Policy, please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-900 font-semibold mb-2">Compare Before You Buy</p>
              <p className="text-gray-700">91 CC/A Phase 4 DHA Lahore</p>
              <p className="text-gray-700">Lahore, 54000</p>
              <p className="text-gray-700">Pakistan</p>
              <p className="text-gray-700 mt-4">
                Email: <a href="mailto:contact@comparebeforeyoubuy.shop" className="text-blue-600 hover:underline">contact@comparebeforeyoubuy.shop</a>
              </p>
              <p className="text-gray-700">
                Phone: <a href="tel:08006521866" className="text-blue-600 hover:underline">08006521866</a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
